import React, { useState } from 'react';
import PopupContainer from '../../components/PopupContainer';
import { DeleteFileConfirmation } from '../../components/ConfirmationPopup';
import { AccentButton } from '../../components/Button';
import fileDeleteIcon from '../../../icons/icon-file-delete.png';

const PopupDemo = () => {
  const [isSimpleOpen, setIsSimpleOpen] = useState(false);
  const [isContentOpen, setIsContentOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteV2Open, setIsDeleteV2Open] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            PopupContainer Demo
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            A simple popup container with backdrop, escape key, and backdrop click handlers
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Test Popups
          </h2>
          
          <div className="flex flex-wrap gap-4">
            <AccentButton onClick={() => setIsSimpleOpen(true)}>
              Simple Popup
            </AccentButton>
            <AccentButton onClick={() => setIsContentOpen(true)}>
              Content Popup
            </AccentButton>
            <AccentButton onClick={() => setIsFormOpen(true)}>
              Form Popup
            </AccentButton>
            <AccentButton onClick={() => setIsDeleteModalOpen(true)}>
              Delete Confirmation (Component)
            </AccentButton>
            <AccentButton onClick={() => setIsDeleteV2Open(true)}>
              Delete Confirmation (V2)
            </AccentButton>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Features
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Click backdrop to close</li>
            <li>Press Escape key to close</li>
            <li>Body scroll is locked when open</li>
            <li>Centered content</li>
            <li>Minimal HTML and Tailwind styles</li>
          </ul>
        </div>
      </div>

      <PopupContainer isOpen={isSimpleOpen} onClose={() => setIsSimpleOpen(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Simple Popup
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This is a simple popup with minimal content. Click outside or press Escape to close.
          </p>
          <AccentButton onClick={() => setIsSimpleOpen(false)}>
            Close
          </AccentButton>
        </div>
      </PopupContainer>

      <PopupContainer isOpen={isContentOpen} onClose={() => setIsContentOpen(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-lg w-full mx-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Content Popup
          </h3>
          <div className="text-gray-600 dark:text-gray-400 mb-6 space-y-4">
            <p>
              This popup contains more content to demonstrate how the container handles
              larger content areas.
            </p>
            <p>
              The backdrop click and escape key functionality work the same way regardless
              of the content size.
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded">
              <p className="text-sm">
                Try clicking outside this popup or pressing Escape to close it.
              </p>
            </div>
          </div>
          <AccentButton onClick={() => setIsContentOpen(false)}>
            Close
          </AccentButton>
        </div>
      </PopupContainer>

      <PopupContainer isOpen={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Form Popup
          </h3>
          <form className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Enter your email"
              />
            </div>
          </form>
          <div className="flex gap-4">
            <AccentButton onClick={() => setIsFormOpen(false)}>
              Submit
            </AccentButton>
            <button
              onClick={() => setIsFormOpen(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            >
              Cancel
            </button>
          </div>
          </div>
        </PopupContainer>

      <PopupContainer
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteFileConfirmation
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => {
            console.log('File deleted via PopupContainer');
            setIsDeleteModalOpen(false);
          }}
          fileName="example.txt"
        />
      </PopupContainer>

      <PopupContainer isOpen={isDeleteV2Open} onClose={() => setIsDeleteV2Open(false)}>
        <div
          className="
            w-[320px]
            bg-white dark:bg-[#121212]
            border border-[#ECECEC] dark:border-[#202020]
            rounded-[32px]
            shadow-[0_26px_38px_0_rgba(18,18,18,0.05)]
            flex flex-col
            relative
            mx-4
          "
        >
          {/* Icon - 80x80px, positioned at y: -40 (overlapping top section) */}
          <div className="absolute top-[-40px] left-1/2 transform -translate-x-1/2 z-10">
            <img 
              src={fileDeleteIcon}
              alt="Delete file"
              className="w-[80px] h-[80px]"
            />
          </div>

          {/* Top Section */}
          <div className="relative flex flex-col items-center pt-[40px] pb-0 px-0 h-[160px] bg-white dark:bg-[#121212] rounded-t-[32px]">
            {/* Text Content - starts 24px below icon */}
            <div className="flex flex-col items-center px-[30px] mt-[24px] mb-0">
              {/* Title */}
              <h2
                className="
                  font-medium text-2xl leading-8 tracking-[-0.173px]
                  text-center
                  text-[#121212] dark:text-[#D0D0D0]
                  mb-[8px]
                "
              >
                Delete this files?
              </h2>

              {/* Description */}
              <p
                className="
                  text-xs leading-4 tracking-[-0.086px]
                  text-center
                  text-[#7B7B7B] dark:text-[#848484]
                "
              >
                Deleted files go to the trash bin and will be stored there for 30 days.
              </p>
            </div>
          </div>

          {/* Bottom Section */}
          <div
            className="
              flex items-center justify-between
              px-[24px] py-[24px]
              h-[84px]
              bg-[#FCFCFC] dark:bg-[#1A1A1A]
              border-t border-[#ECECEC] dark:border-[#202020]
              rounded-b-[32px]
            "
          >
            {/* Cancel Button */}
            <AccentButton
              variant="secondary"
              onClick={() => setIsDeleteV2Open(false)}
              className="w-[130px] h-[36px]"
            >
              Cancel
            </AccentButton>

            {/* Confirm Button */}
            <AccentButton
              variant="orange"
              onClick={() => {
                console.log('File deleted via PopupContainer');
                setIsDeleteV2Open(false);
              }}
              className="w-[130px] h-[36px]"
            >
              Yes, delete it
            </AccentButton>
          </div>
        </div>
      </PopupContainer>
    </div>
  );
};

export default PopupDemo;

